import Component from '@glimmer/component';
import { assert } from '@ember/debug';
import { LinkTo } from '@ember/routing';
import { PhCaretRight } from 'ember-phosphor-icons';
import { and, not } from 'tio-ui/utilities';
import Heading from './heading';
import IconHeading from './icon-heading';
import Text from './text';
import type { TOC } from '@ember/component/template-only';
import type { WithBoundArgs } from '@glint/template';
import type { ComponentLike } from '@glint/template';
import type Owner from '@ember/owner';

interface CrumbSignature {
  Args: {
    /** Display label */
    label: string;
    /** Route name for navigation (omit for current/terminal crumb) */
    route?: string;
    /** Route model for dynamic segments */
    model?: unknown;
  };
  Element: HTMLLIElement;
}

/**
 * Breadcrumb crumb — renders a link or static label.
 * The trailing caret separator auto-hides on the last crumb via `last:hidden`.
 */
const Crumb: TOC<CrumbSignature> = <template>
  <li
    class="tio-inline-row-extra-tight tio-type-body-secondary [&:last-of-type>.crumb-sep]:hidden"
    ...attributes
  >
    {{#if @route}}
      {{#if @model}}
        <LinkTo
          @route={{@route}}
          @model={{@model}}
          class="tio-page-header__crumb-link transition-colors"
        >
          {{@label}}
        </LinkTo>
      {{else}}
        <LinkTo @route={{@route}} class="tio-page-header__crumb-link transition-colors">
          {{@label}}
        </LinkTo>
      {{/if}}
    {{else}}
      <span class="tio-page-header__crumb-current">{{@label}}</span>
    {{/if}}
    <PhCaretRight
      @size={{14}}
      class="tio-page-header__crumb-separator crumb-sep shrink-0"
      aria-hidden="true"
    />
  </li>
</template>;

interface TitleGroupSignature {
  Args: {
    /** Visible page heading. */
    title: string;
    /** Supporting text rendered beneath the heading. */
    subtitle?: string;
    /** Decorative Phosphor icon aligned to the heading line box. */
    icon?: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLDivElement;
}

const TitleGroup: TOC<TitleGroupSignature> = <template>
  <div data-test-page-header-title-group class="tio-page-header__title-group min-w-0">
    <IconHeading
      @icon={{@icon}}
      @iconClass="tio-fg-muted"
      data-test-page-header-icon={{if @icon ""}}
    >
      <Heading data-test-page-header-title @level={{1}} @role="page-title" class="min-w-0">
        {{#if (has-block)}}{{yield}}{{else}}{{@title}}{{/if}}
      </Heading>
    </IconHeading>
    {{#if @subtitle}}
      <Text
        data-test-page-header-subtitle
        @variant="muted"
        @role="body-secondary"
        class="tio-page-header__subtitle {{if @icon 'tio-page-header__subtitle--with-icon'}}"
      >
        {{@subtitle}}
      </Text>
    {{/if}}
  </div>
</template>;

interface PageHeaderSignature {
  Args: {
    /** Page title — rendered as the page's visible semantic h1. With
     * `:breadcrumbs` the trail renders above it; the title stays visible. */
    title?: string;
    /** Accessible title for screen readers — the sr-only h1 when @title is
     * omitted. With `:breadcrumbs` this is the compact-mode opt-in: the trail
     * alone names the page visually. Required when @title is not provided. */
    a11yTitle?: string;
    /** Optional subtitle below the title or breadcrumbs */
    subtitle?: string;
    /** Optional Phosphor icon aligned to the visible title's line box */
    icon?: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
    /**
     * Sticky at the top while the page scrolls (the default). Pass false on
     * pages whose header should scroll away with the content.
     */
    sticky?: boolean;
    /** Opts into the compact list-page header recipe without changing other page types. */
    layout?: 'list';
    /** Set false when a wrapper forwards an absent toolbar block. Defaults to true. */
    showToolbar?: boolean;
    /** Set false when a wrapper forwards an absent below block. Defaults to true. */
    showBelow?: boolean;
    /** Hide supplementary copy below 48rem when the title already identifies the task. */
    hideSubtitleOnMobile?: boolean;
  };
  Blocks: {
    /** Optional custom content rendered inside the semantic page title. */
    title?: [];
    /** Breadcrumb navigation — yields `{ crumb }` for building a breadcrumb trail */
    breadcrumbs?: [{ crumb: WithBoundArgs<typeof Crumb, 'model'> }];
    /** Center toolbar area between title and actions (search, filters, etc.) */
    toolbar?: [];
    /** Right-side actions area (buttons, tools, dropdowns) */
    actions?: [];
    /** Content below the title bar (tabs, filters, search, etc.) */
    below?: [];
  };
  Element: HTMLElement;
}

/**
 * Establishes the page h1 with optional breadcrumbs, actions, and a below-title slot for tabs or
 * filters.
 */
export default class PageHeader extends Component<PageHeaderSignature> {
  constructor(owner: Owner, args: PageHeaderSignature['Args']) {
    super(owner, args);
    assert(
      '<PageHeader> requires either @title or @a11yTitle to ensure every page has a semantic h1 for accessibility.',
      args.title || args.a11yTitle
    );
  }

  get isStatic() {
    return this.args.sticky === false;
  }

  <template>
    <header
      data-test-page-header
      class="app-page-header
        {{if (eq @layout 'list') 'app-page-header--list'}}
        {{if @hideSubtitleOnMobile 'app-page-header--mobile-title-only'}}
        {{if this.isStatic 'app-page-header--static'}}
        {{if (and (has-block 'breadcrumbs') (not @title)) 'app-page-header--compact'}}"
      ...attributes
    >
      {{#if (and (has-block "breadcrumbs") (not @title))}}
        {{! Compact mode uses the final breadcrumb visually and a11yTitle for the hidden h1. }}
        <Heading data-test-page-header-title @level={{1}} @role="page-title" class="sr-only">
          {{@a11yTitle}}
        </Heading>
        <div class="tio-wrap-loose items-center">
          {{! Sized by its content, not from zero: the actions then wrap below a trail they would overlap. }}
          <div class="tio-row-tight min-w-0 flex-auto">
            {{#if @icon}}
              {{#let @icon as |Icon|}}
                <Icon
                  data-test-page-header-compact-icon
                  @size={{24}}
                  class="tio-icon-default tio-fg-muted shrink-0"
                />
              {{/let}}
            {{/if}}
            <nav data-test-page-header-breadcrumbs aria-label="Breadcrumbs" class="min-w-0">
              <ol class="tio-row-flush">
                {{yield (hash crumb=(component Crumb)) to="breadcrumbs"}}
              </ol>
              {{#if @subtitle}}
                <Text
                  data-test-page-header-subtitle
                  @variant="muted"
                  @role="caption"
                  class="tio-page-header__subtitle"
                >
                  {{@subtitle}}
                </Text>
              {{/if}}
            </nav>
          </div>

          {{#if (and (has-block "toolbar") (not (eq @showToolbar false)))}}
            <div data-test-page-header-toolbar class="tio-page-header__toolbar">
              <div class="tio-page-header__toolbar-inner">
                {{yield to="toolbar"}}
              </div>
            </div>
          {{/if}}

          {{#if (has-block "actions")}}
            <div data-test-page-header-actions class="tio-row-tight shrink-0 ml-auto">
              {{yield to="actions"}}
              <div id="page-header-actions-portal" class="contents"></div>
            </div>
          {{else}}
            <div class="app-page-header__portal-actions tio-row-tight shrink-0 ml-auto">
              <div
                id="page-header-actions-portal"
                class="app-page-header__actions-target contents"
              ></div>
            </div>
          {{/if}}
        </div>
      {{else}}
        {{#if (has-block "breadcrumbs")}}
          {{! Breadcrumbs supplement the visible h1. }}
          <nav
            data-test-page-header-breadcrumbs
            aria-label="Breadcrumbs"
            class="tio-page-header__breadcrumbs min-w-0"
          >
            <ol class="tio-row-flush">
              {{yield (hash crumb=(component Crumb)) to="breadcrumbs"}}
            </ol>
          </nav>
        {{/if}}
        {{#if @title}}
          <div
            data-test-page-header-title-row
            class="app-page-header__identity tio-wrap-loose items-center"
          >
            {{#if (has-block "title")}}
              <TitleGroup @title={{@title}} @subtitle={{@subtitle}} @icon={{@icon}}>
                {{yield to="title"}}
              </TitleGroup>
            {{else}}
              <TitleGroup @title={{@title}} @subtitle={{@subtitle}} @icon={{@icon}} />
            {{/if}}

            {{#if (and (has-block "toolbar") (not (eq @showToolbar false)))}}
              <div data-test-page-header-toolbar class="tio-page-header__toolbar">
                <div class="tio-page-header__toolbar-inner">
                  {{yield to="toolbar"}}
                </div>
              </div>
            {{/if}}

            {{#if (has-block "actions")}}
              <div
                data-test-page-header-actions
                class="app-page-header__actions tio-row-tight ml-auto shrink-0"
              >
                {{yield to="actions"}}
                <div id="page-header-actions-portal" class="contents"></div>
              </div>
            {{else}}
              <div class="app-page-header__portal-actions tio-row-tight ml-auto shrink-0">
                <div
                  id="page-header-actions-portal"
                  class="app-page-header__actions-target contents"
                ></div>
              </div>
            {{/if}}
          </div>
        {{else}}
          <Heading data-test-page-header-title @level={{1}} @role="page-title" class="sr-only">
            {{@a11yTitle}}
          </Heading>
          <div class="tio-wrap-loose items-center">
            {{#if (and (has-block "toolbar") (not (eq @showToolbar false)))}}
              <div data-test-page-header-toolbar class="tio-page-header__toolbar">
                <div class="tio-page-header__toolbar-inner">
                  {{yield to="toolbar"}}
                </div>
              </div>
            {{/if}}

            {{#if (has-block "actions")}}
              <div data-test-page-header-actions class="tio-row-tight shrink-0 ml-auto">
                {{yield to="actions"}}
                <div id="page-header-actions-portal" class="contents"></div>
              </div>
            {{else}}
              <div class="app-page-header__portal-actions tio-row-tight shrink-0 ml-auto">
                <div
                  id="page-header-actions-portal"
                  class="app-page-header__actions-target contents"
                ></div>
              </div>
            {{/if}}
          </div>
        {{/if}}
      {{/if}}

      {{! Negate header gutters and block padding so a nested PageControlBar aligns with the title. }}
      {{#if (and (has-block "below") (not (eq @showBelow false)))}}
        <div data-test-page-header-below class="app-page-header__below">
          {{yield to="below"}}
        </div>
      {{/if}}
    </header>
  </template>
}

export { Crumb as PageHeaderCrumb };
export type { PageHeaderSignature, CrumbSignature as PageHeaderCrumbSignature };
